import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Checkbox,
  FormControlLabel,
  TableCellProps,
} from "@material-ui/core";
import ClinicalReviewPageWrapper from "./ClinicalReviewPageWrapper";
import {
  Order as OrderTypes,
  ProductDetails,
  IdChecks,
  PreApprovedProductCustomerList,
} from "./Types";
import "./index.scss";
import { Typography } from "@welldigital/components";
import CustomTable, { CustomTableHeaderProps } from "./components/Table";
import { formatOrderStatus } from "utils/user";
import { formatDate } from "utils/time";
import Pagination, { PaginationProps } from "./components/Pagination";
import {
  ClinicalReviewStatus,
  ClinicalReviewRouteCategory,
  CLINICAL_REVIEW_GSL_CATEGORY,
  CLINICAL_REVIEW_ID_CHECK_CATEGORY,
  CLINICAL_REVIEW_POM_CATEGORY,
  CLINICAL_REVIEW_PREAPPROVED_PRODUCT_CATEGORY,
  CLINICAL_REVIEW_STATUSES,
  isClinicalReviewNonReviewTagCategory,
  isClinicalReviewSpecialCategory,
} from "./constants";

export interface ClinicalReviewPageProps extends PaginationProps {
  orderList: OrderTypes[];
  status: ClinicalReviewStatus; // "PREAPPROVEDPRODUCT" is not any status, this is to differenciate the PREAPPROVEDPRODUCT landing page.
  category: ClinicalReviewRouteCategory | undefined;
  sortKey: string;
  isAscending: boolean;
  hideSubscriptions: boolean;
  loading?: boolean;
  onSortChange: (sortKeyName: string) => void;
  onHideSubscriptionsChange: (hideSubscriptions: boolean) => void;
  idChecks: IdChecks[];
  preApprovedProductCustomerList: PreApprovedProductCustomerList[];
}

export const ClinicalReviewOrderList: React.FC<ClinicalReviewPageProps> = ({
  orderList,
  status,
  category,
  loading,
  sortKey,
  isAscending,
  hideSubscriptions,
  onSortChange,
  onHideSubscriptionsChange,
  idChecks,
  preApprovedProductCustomerList,
  ...paginationProps
}) => {
  const columns: CustomTableHeaderProps<OrderTypes>[] = useMemo(() => {
    const columns: CustomTableHeaderProps<OrderTypes>[] =
      !isClinicalReviewSpecialCategory(category)
        ? [
            {
              header: "Order ID",
              keyExtractor: "id",
              cellProps: { ...CELL_PROPS, width: "22%" },
              transform: (data) => {
                const subId = data.products?.find(
                  (product) => !!product.subscriptionId,
                );
                return (
                  <>
                    <Link to={`/clinical-review/order/${data.id}`}>
                      {data.id}
                    </Link>
                    <Typography>
                      {subId ? `Contains subscription product` : ""}
                    </Typography>
                  </>
                );
              },
            },
            {
              header: "POM/PMed",
              keyExtractor: "categories",
              cellProps: { ...CELL_PROPS, width: "12%" },
              isSortable: true,
              sortKeyName: "category",
              onSortChange,
              transform: (data) => {
                console.log("data?.categories: ", data?.categories)
                return (
                  <>
                    {status !== CLINICAL_REVIEW_STATUSES.COMPLETED ? (
                      <>
                        {data?.categories?.length === 1 &&
                        data.categories[0] === CLINICAL_REVIEW_GSL_CATEGORY
                          ? data.categories[0]
                          : data.categories
                              ?.filter(
                                (categ) =>
                                  !isClinicalReviewNonReviewTagCategory(categ),
                              )
                              ?.join(" and ")}
                      </>
                    ) : (
                      <>
                        {filterProducts(data.products)?.map(
                          (product, index) => (
                            <div
                              className="multi-line-info"
                              key={`category_${index}`}
                            >
                              {product.primaryTag}
                            </div>
                          ),
                        )}
                      </>
                    )}
                  </>
                );
              },
            },
            {
              header: "Status",
              keyExtractor: "orderStatus",
              cellProps: { ...CELL_PROPS, width: "13%" },
              transform: (data) => formatOrderStatus(data.orderStatus),
            },
            {
              header: "Order Date",
              keyExtractor: "orderdate",
              isSortable: true,
              sortKeyName: "date",
              onSortChange,
              cellProps: { ...CELL_PROPS, width: "12%" },
              transform: (data) => formatDate(data.orderdate, "dd/MM/yy"),
            },
          ]
        : [];
    if (!isClinicalReviewSpecialCategory(category)) {
      if (status === CLINICAL_REVIEW_STATUSES.COMPLETED) {
        columns.push(
          {
            header: "Item",
            keyExtractor: "products",
            transform: (data) =>
              filterProducts(data.products)?.map((product, index) => (
                <div
                  className="multi-line-info"
                  key={`item_${data.id}_${index}`}
                >
                  {product.productTitle}
                </div>
              )),
          },
          {
            header: "Approved/Rejected",
            keyExtractor: "products",
            transform: (data) =>
              filterProducts(data.products)?.map((product, index) => (
                <div
                  className="multi-line-info"
                  key={`status_${data.id}_${index}`}
                >
                  <span>{formatOrderStatus(product.reviewStatus)}</span>
                  <span>
                    {product.reviewDate &&
                      formatDate(product.reviewDate, "dd/MM/yy HH:mm")}{" "}
                    <span className="text-lower">{product.reviewedBy}</span>
                  </span>
                </div>
              )),
          },
        );
      } else {
        columns.push(
          {
            header: "Shipping method",
            keyExtractor: "shippingMethod",
            cellProps: { ...CELL_PROPS, width: "10%" },
            transform: (data) => data.shippingMethod,
          },
          {
            header: "Warnings",
            keyExtractor: "idCheckStatus" || "warnings",
            cellProps: { ...CELL_PROPS, width: "8%" },
            transform: (data) =>
              data.warnings
                ? "Warnings"
                : data?.idCheckStatus !== "" &&
                  data?.idCheckStatus.toLowerCase() !== "pass"
                ? data.idCheckStatus
                : data?.idCheckApiStatus?.toLowerCase() === "failure"
                ? "API failure"
                : "",
          },
          {
            header: "Comments",
            keyExtractor: "comment",
            transform: (data) =>
              data.comment?.text ? (
                <span>
                  {data.comment?.createdOn &&
                    formatDate(data.comment?.createdOn, "dd/MM/yy HH:mm")}{" "}
                  <span className="text-lower">{data.comment?.createdBy}</span>{" "}
                  {data.comment?.text}
                </span>
              ) : (
                ""
              ),
          },
        );
      }
    }
    return columns;
  }, [status, category, onSortChange]);

  const idCheckColumns: CustomTableHeaderProps<IdChecks>[] = useMemo(() => {
    const idCheckColumns: CustomTableHeaderProps<IdChecks>[] =
      category === CLINICAL_REVIEW_ID_CHECK_CATEGORY
        ? [
            {
              header: "User ID",
              keyExtractor: "userId",
              cellProps: { ...CELL_PROPS, width: "13%" },
              // transform: (data) => formatOrderStatus(data.userId),
            },
            {
              header: "Order ID",
              keyExtractor: "orderId",
              cellProps: { ...CELL_PROPS, width: "13%" },
              transform: (data) => (
                <Link to={`/clinical-review/order/${data.orderId}`}>
                  {data.orderId}
                </Link>
              ),
            },
            {
              header: "Order date",
              keyExtractor: "orderDate",
              cellProps: { ...CELL_PROPS, width: "13%" },
              transform: (data) => formatDate(data.orderDate, "dd/MM/yy"),
            },
          ]
        : [];
    if (category === CLINICAL_REVIEW_ID_CHECK_CATEGORY) {
      if (status === CLINICAL_REVIEW_STATUSES.AWAITING_ID_CHECK) {
        idCheckColumns.push({
          header: "Warnings",
          keyExtractor: "idCheckApiStatus",
          cellProps: { ...CELL_PROPS, width: "13%" },
          transform: (data) => {
            return data.idCheckApiStatus?.toLowerCase() === "failure"
              ? "API failure"
              : "";
          },
        });
      } else {
        idCheckColumns.push({
          header: "ID check status",
          keyExtractor: "idCheckStatus",
          cellProps: { ...CELL_PROPS, width: "13%" },
          // transform: (data) => formatOrderStatus(data.orderStatus),
        });
      }
    }
    return idCheckColumns;
  }, [status, category]);

  const preApprovedProductColumns: CustomTableHeaderProps<PreApprovedProductCustomerList>[] =
    useMemo(() => {
      const preApprovedProductColumns: CustomTableHeaderProps<PreApprovedProductCustomerList>[] =
        category === CLINICAL_REVIEW_PREAPPROVED_PRODUCT_CATEGORY
          ? [
              {
                header: "User ID",
                keyExtractor: "cognitoId",
                cellProps: { ...CELL_PROPS, width: "30%" },
                transform: (data) => (
                  <Link
                    to={`/clinical-review/customer/${data.id}?treatment=${data.treatmentName}`}
                  >
                    {data.cognitoId}
                  </Link>
                ),
              },
              {
                header: "Customer name",
                keyExtractor: "firstName",
                cellProps: { ...CELL_PROPS, width: "20%" },
                transform: (data) => `${data.firstName} ${data.lastName}`,
              },
              {
                header: "Treatment",
                keyExtractor: "treatmentName",
                cellProps: { ...CELL_PROPS, width: "10%" },
                transform: (data) => `${data.treatmentName}`,
              },
              {
                header: "Last order approval date",
                keyExtractor: "lastApprovalDate",
                isSortable: true,
                sortKeyName: "lastApprovalDate",
                onSortChange,
                cellProps: { ...CELL_PROPS, width: "20%" },
                transform: (data) =>
                  data?.lastApprovalDate &&
                  formatDate(data.lastApprovalDate, "dd/MM/yy"),
              },
              {
                header: "Next review due",
                keyExtractor: "nextReviewDate",
                isSortable: true,
                sortKeyName: "nextReviewDate",
                onSortChange,
                cellProps: { ...CELL_PROPS, width: "20%" },
                transform: (data) =>
                  data?.nextReviewDate &&
                  formatDate(data.nextReviewDate, "dd/MM/yy"),
              },
            ]
          : [];

      return preApprovedProductColumns;
    }, [category, onSortChange]);

  const title = useMemo(() => {
    return status === CLINICAL_REVIEW_STATUSES.COMPLETED
      ? `Completed Orders`
      : status === CLINICAL_REVIEW_STATUSES.AWAITING_ID_CHECK
      ? `Awaiting ID check`
      : status === CLINICAL_REVIEW_STATUSES.ID_CHECK_COMPLETED
      ? `ID check completed`
      : status === CLINICAL_REVIEW_STATUSES.PREAPPROVED_PRODUCT
      ? `Weight management patients`
      : `Awaiting clinical review`;
  }, [status]);

  const categoryTitle = useMemo(() => {
    return isClinicalReviewSpecialCategory(category)
      ? ``
      : category
      ? `(${category}s)`
      : "(all)";
  }, [category]);

  const showHideSubscriptions =
    status === CLINICAL_REVIEW_STATUSES.COMPLETED &&
    category === CLINICAL_REVIEW_POM_CATEGORY;

  return (
    <div className="crp-dashboard-container">
      <Box className="crp-dashboard-header">
        <Typography className="crp-dashboard-title">
          {title} {categoryTitle}
        </Typography>
        {showHideSubscriptions && (
          <FormControlLabel
            className="crp-dashboard-filter"
            control={
              <Checkbox
                checked={hideSubscriptions}
                color="primary"
                onChange={(event) =>
                  onHideSubscriptionsChange(event.target.checked)
                }
              />
            }
            label="Hide Subscriptions"
          />
        )}
      </Box>
      {category === CLINICAL_REVIEW_ID_CHECK_CATEGORY ? (
        <CustomTable
          sortKey={sortKey}
          isAscending={isAscending}
          data={idChecks}
          loading={loading}
          columns={idCheckColumns}
        />
      ) : category === CLINICAL_REVIEW_PREAPPROVED_PRODUCT_CATEGORY ? (
        <CustomTable
          sortKey={sortKey}
          isAscending={isAscending}
          data={preApprovedProductCustomerList}
          loading={loading}
          columns={preApprovedProductColumns}
        />
      ) : (
        <CustomTable
          sortKey={sortKey}
          isAscending={isAscending}
          data={orderList}
          loading={loading}
          columns={columns}
        />
      )}

      <Pagination status={status} category={category} {...paginationProps} />
    </div>
  );
};

export default ClinicalReviewPageWrapper(ClinicalReviewOrderList);

const CELL_PROPS: TableCellProps = { align: "left" };

const filterProducts = (products?: ProductDetails[]) => {
  return products?.filter(
    (product) => product.primaryTag !== CLINICAL_REVIEW_GSL_CATEGORY,
  );
};
